import React, { useEffect, useState } from "react";
import {
  useAddConfirmMutation,
  useGetTodosQuery,
  useDeleteTodoMutation,
  useUpdateTodoMutation,
} from "../../redux/slices/productApiSlice";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Basket = () => {
  const navigate = useNavigate();

  // =========================================================
  // CART - RTK QUERY
  // =========================================================
  const {
    data,
    isLoading,
    isError,
    error: basketQueryError,
    refetch,
  } = useGetTodosQuery();

  const [updateTodo] = useUpdateTodoMutation();
  const [addConfirm] = useAddConfirmMutation();
  const [removeTodo] = useDeleteTodoMutation();

  // =========================================================
  // LOCAL UI STATE
  // =========================================================
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [updatingProductId, setUpdatingProductId] =
    useState(null);

  const [removingProductId, setRemovingProductId] =
    useState(null);

  const [confirmingCart, setConfirmingCart] =
    useState(false);

  // =========================================================
  // ERROR MESSAGE
  // =========================================================
  const getErrorMessage = (err) => {
    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.data?.message ||
      err?.data?.message ||
      err?.data?.error;

    if (serverMessage) {
      return String(serverMessage);
    }

    if (
      err?.status === 400 ||
      err?.response?.status === 400
    ) {
      return "Göndərilən məlumatlar düzgün deyil. Zəhmət olmasa, yenidən cəhd edin.";
    }

    if (
      err?.status === 401 ||
      err?.response?.status === 401
    ) {
      return "Bu əməliyyatı yerinə yetirmək üçün hesabınıza daxil olmalısınız.";
    }

    if (
      err?.status === 403 ||
      err?.response?.status === 403
    ) {
      return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
    }

    if (
      err?.status === 404 ||
      err?.response?.status === 404
    ) {
      return "Məlumat tapılmadı. Məhsul artıq mövcud olmaya bilər.";
    }

    if (
      err?.status === 409 ||
      err?.response?.status === 409
    ) {
      return "Bu əməliyyat mümkün olmadı. Məhsulun məlumatları artıq dəyişmiş ola bilər.";
    }

    if (
      err?.status === 429 ||
      err?.response?.status === 429
    ) {
      return "Çox sayda sorğu göndərildi. Bir qədər gözləyib yenidən cəhd edin.";
    }

    if (
      err?.status >= 500 ||
      err?.response?.status >= 500
    ) {
      return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
    }

    if (
      err?.message === "Network Error" ||
      err?.error === "FETCH_ERROR"
    ) {
      return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
    }

    if (err?.message) {
      return err.message;
    }

    return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
  };

  // =========================================================
  // PRODUCT SELECTION
  // =========================================================
  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  // =========================================================
  // QUANTITY UPDATE
  // =========================================================
  const updateQuantity = async (
    productId,
    quantity
  ) => {
    if (!productId) {
      toast.error(
        "Məhsulun identifikatoru tapılmadı."
      );
      return;
    }

    if (quantity < 1) {
      return;
    }

    if (updatingProductId !== null) {
      return;
    }

    setUpdatingProductId(productId);

    try {
      await updateTodo({
        productId,
        quantity,
      }).unwrap();

      /*
        Burada artıq refetch() yoxdur.

        productApiSlice:

        updateTodo
        ↓
        invalidatesTags: ["Cart"]
        ↓
        useGetTodosQuery avtomatik yenilənir
        ↓
        Basket yeni məlumatı göstərir
      */

      toast.success(
        "Məhsulun miqdarı uğurla yeniləndi."
      );
    } catch (err) {
      console.error(
        "Məhsulun miqdarı yenilənmədi:",
        err
      );

      toast.error(
        `Məhsulun miqdarını yeniləmək mümkün olmadı.\n\nSəbəb: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setUpdatingProductId(null);
    }
  };

  // =========================================================
  // REMOVE PRODUCT
  // =========================================================
  const removeProduct = async (_id) => {
    if (!_id) {
      toast.error(
        "Məhsulun identifikatoru tapılmadı."
      );
      return;
    }

    if (removingProductId !== null) {
      return;
    }

    setRemovingProductId(_id);

    try {
      await removeTodo(_id).unwrap();

      /*
        refetch() artıq lazım deyil.

        removeTodo
        ↓
        invalidatesTags: ["Cart"]
        ↓
        useGetTodosQuery avtomatik yenilənir
      */

      // Silinən məhsulu seçimdən çıxarırıq
      setSelectedProducts((prev) =>
        prev.filter((id) => id !== _id)
      );

      toast.success(
        "Məhsul səbətdən uğurla silindi."
      );
    } catch (err) {
      console.error(
        "Məhsul səbətdən silinmədi:",
        err
      );

      const reason = getErrorMessage(err);

      toast.error(
        `Məhsulu səbətdən silmək mümkün olmadı.\n\nSəbəb: ${reason}`
      );
    } finally {
      setRemovingProductId(null);
    }
  };

  // =========================================================
  // CONFIRM CART
  // =========================================================
  const handleConfirmCart = async () => {
    try {
      if (!Array.isArray(data)) {
        toast.error(
          "Səbətdəki məhsulların məlumatlarını əldə etmək mümkün olmadı. Zəhmət olmasa, yenidən cəhd edin."
        );
        return;
      }

      const selectedItems = data.filter((product) =>
        selectedProducts.includes(product._id)
      );

      if (selectedItems.length === 0) {
        toast.info(
          "Zəhmət olmasa, ödənişə keçmək üçün ən azı bir məhsul seçin."
        );
        return;
      }

      const unavailableProduct =
        selectedItems.find(
          (product) => product.stock === 0
        );

      if (unavailableProduct) {
        toast.warning(
          `"${unavailableProduct.title}" məhsulu hazırda stokda yoxdur. Bu məhsulu seçimdən çıxardıqdan sonra səbəti təsdiqləyə bilərsiniz.`
        );
        return;
      }

      setConfirmingCart(true);

      const response = await addConfirm({
        products: selectedItems,
      }).unwrap();

      const confirmedCartId =
        response.confirmedCartId;

      if (!confirmedCartId) {
        throw new Error(
          "Təsdiqlənmiş səbətin identifikatoru serverdən alınmadı."
        );
      }

      toast.success(
        "Səbət uğurla təsdiqləndi. Ödəniş səhifəsinə yönləndirilirsiniz."
      );

      setTimeout(() => {
        navigate(
          `/payment?confirmedCartId=${confirmedCartId}`
        );
      }, 1000);
    } catch (err) {
      console.error(
        "Səbət təsdiqlənərkən xəta baş verdi:",
        err
      );

      const reason = getErrorMessage(err);

      toast.error(
        `Səbəti təsdiqləmək mümkün olmadı.\n\nSəbəb: ${reason}`
      );
    } finally {
      setConfirmingCart(false);
    }
  };

  // =========================================================
  // SYNC SELECTED PRODUCTS WITH CART DATA
  // =========================================================
  useEffect(() => {
    if (!Array.isArray(data)) {
      return;
    }

    /*
      Əvvəlki kodda burada:

      dispatch(setTodos(data));

      var idi.

      Artıq Redux cart state istifadə etmirik.
      RTK Query əsas source of truth-dur.
    */

    setSelectedProducts((prevSelected) => {
      const currentProductIds = data.map(
        (product) => product._id
      );

      // Səbət boşdursa seçimləri təmizlə
      if (currentProductIds.length === 0) {
        return [];
      }

      /*
        Mövcud seçimlərdən səbətdə hələ də olanları saxlayırıq.
        Yeni əlavə olunmuş məhsullar avtomatik seçilsin deyə
        onları da əlavə edirik.
      */

      const validSelected = prevSelected.filter(
        (id) => currentProductIds.includes(id)
      );

      const newProductIds =
        currentProductIds.filter(
          (id) => !prevSelected.includes(id)
        );

      return [
        ...validSelected,
        ...newProductIds,
      ];
    });
  }, [data]);

  // =========================================================
  // TOTAL PRICE
  // =========================================================
  const calculateTotalPrice = Array.isArray(data)
    ? Math.round(
        data
          .filter((product) =>
            selectedProducts.includes(
              product._id
            )
          )
          .reduce(
            (total, product) =>
              total +
              (product.price || 0) *
                (product.quantity || 1),
            0
          ) * 100
      ) / 100
    : 0;

  // =========================================================
  // ORIGINAL PRICE
  // =========================================================
  const calculateTotalOrginalPrice =
    Array.isArray(data)
      ? Math.round(
          data
            .filter((product) =>
              selectedProducts.includes(
                product._id
              )
            )
            .reduce(
              (total, product) =>
                total +
                (product.orginalPrice ||
                  product.price ||
                  0) *
                  (product.quantity || 1),
              0
            ) * 100
        ) / 100
      : 0;

  // =========================================================
  // SELECTED ITEMS
  // =========================================================
  const selectedItems = Array.isArray(data)
    ? data.filter((product) =>
        selectedProducts.includes(
          product._id
        )
      )
    : [];

  // =========================================================
  // STOCK
  // =========================================================
  const isStockAvailable =
    selectedItems.length > 0 &&
    !selectedItems.some(
      (product) => product.stock === 0
    );

  // =========================================================
  // LOADING
  // =========================================================
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center px-4 bg-gray-50 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-5"></div>

        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
          Səbətiniz yüklənir...
        </h2>

        <p className="text-center text-gray-500 dark:text-gray-400 max-w-md">
          Səbətinizdəki məhsullar hazırlanır. Zəhmət olmasa, gözləyin.
        </p>
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================
  if (isError) {
    const queryErrorMessage =
      getErrorMessage(basketQueryError);

    return (
      <div className="min-h-screen flex justify-center items-center px-4 bg-gray-50 dark:bg-gray-900">
        <div className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 md:p-8 border border-red-200 dark:border-red-900">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <span className="text-3xl">
                ⚠️
              </span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 text-center mb-3">
            Səbət yüklənmədi
          </h2>

          <p className="text-center text-gray-600 dark:text-gray-300 mb-5">
            Səbət məlumatlarını əldə etmək mümkün olmadı.
          </p>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4 mb-6">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
              Xətanın səbəbi:
            </p>

            <p className="text-sm text-red-600 dark:text-red-200 break-words whitespace-pre-wrap">
              {queryErrorMessage}
            </p>
          </div>

          <button
            type="button"
            onClick={() => refetch()}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-5 rounded-xl transition duration-200"
          >
            Yenidən cəhd et
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-[740px]">

      {/* Səbət boş olduqda */}
      {Array.isArray(data) &&
      data.length === 0 ? (
        <div className="min-h-[500px] flex justify-center items-center">
          <div className="w-full max-w-md text-center bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="text-6xl mb-5">
              🛒
            </div>

            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">
              Səbətiniz boşdur
            </h2>

            <p className="text-gray-500 dark:text-gray-300 mb-6">
              Hazırda səbətinizdə heç bir məhsul yoxdur.
              Məhsullara baxaraq istədiyiniz məhsulları
              səbətinizə əlavə edə bilərsiniz.
            </p>

            <button
              type="button"
              onClick={() => navigate("/")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition duration-200"
            >
              Məhsullara bax
            </button>
          </div>
        </div>
      ) : (
        <>
          {data &&
            data.map((product) => {
              const isUpdating =
                updatingProductId ===
                product.productId;

              const isRemoving =
                removingProductId ===
                product._id;

              return (
                <div
                  key={product._id}
                  className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-md hover:shadow-xl transition duration-300 p-6 mb-6 flex flex-col md:flex-row md:items-center gap-6"
                >
                  {/* Məhsul seçimi */}
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(
                      product._id
                    )}
                    onChange={() =>
                      toggleProductSelection(
                        product._id
                      )
                    }
                    disabled={
                      isUpdating ||
                      isRemoving ||
                      confirmingCart
                    }
                    aria-label={`${product.title} məhsulunu seç`}
                    className="w-5 h-5 text-green-600 accent-green-500"
                  />

                  {/* Məhsul şəkli */}
                  <div
                    className="w-full md:w-auto flex justify-center"
                    onClick={() =>
                      navigate(
                        `/product/${product.productId}`
                      )
                    }
                  >
                    <img
                      src={
                        Array.isArray(
                          product.photo
                        ) &&
                        product.photo.length > 0
                          ? product.photo[0]
                          : product.photo
                      }
                      alt={product.title}
                      className="w-32 h-32 rounded-full border border-gray-300 object-cover cursor-pointer"
                    />
                  </div>

                  {/* Məhsul məlumatları */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {product.title}
                    </h3>

                    {/* Miqdar */}
                    <div className="flex justify-center md:justify-start items-center space-x-4 mb-2">
                      {product.quantity > 1 ? (
                        <button
                          type="button"
                          disabled={
                            isUpdating ||
                            isRemoving ||
                            confirmingCart
                          }
                          onClick={() =>
                            updateQuantity(
                              product.productId,
                              product.quantity - 1
                            )
                          }
                          className="bg-gray-200 hover:bg-gray-300 disabled:bg-gray-400 disabled:cursor-not-allowed text-gray-800 rounded-full px-4 py-1 font-semibold transition"
                        >
                          {isUpdating
                            ? "..."
                            : "-"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={
                            isRemoving ||
                            isUpdating ||
                            confirmingCart
                          }
                          onClick={() =>
                            removeProduct(
                              product._id
                            )
                          }
                          className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full px-4 py-1 font-semibold transition"
                        >
                          {isRemoving
                            ? "Silinir..."
                            : "Sil"}
                        </button>
                      )}

                      <span className="text-lg font-semibold dark:text-white min-w-[30px]">
                        {isUpdating
                          ? "..."
                          : product.quantity}
                      </span>

                      <button
                        type="button"
                        disabled={
                          isUpdating ||
                          isRemoving ||
                          confirmingCart
                        }
                        onClick={() =>
                          updateQuantity(
                            product.productId,
                            product.quantity + 1
                          )
                        }
                        className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full px-4 py-1 font-semibold transition"
                      >
                        {isUpdating
                          ? "..."
                          : "+"}
                      </button>
                    </div>

                    {/* Ümumi qiymət */}
                    <p className="text-lg font-semibold dark:text-white text-gray-800">
                      Ümumi qiymət:{" "}
                      {(
                        (product.orginalPrice ||
                          product.price ||
                          0) *
                        (product.quantity ||
                          1)
                      ).toFixed(2)}{" "}
                      ₼
                    </p>

                    {/* Stok */}
                    {product.stock === 1 && (
                      <p className="text-red-500 font-medium mt-1">
                        Diqqət: bu məhsuldan cəmi 1 ədəd qalıb.
                      </p>
                    )}

                    {product.stock === 0 && (
                      <p className="text-red-500 font-medium mt-1">
                        Bu məhsul hazırda stokda yoxdur.
                        Səbəti təsdiqləmək üçün bu məhsulu
                        seçimdən çıxarın.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

          {/* Səbətin yekun hissəsi */}
          <div className="mt-12 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-8 flex flex-col md:flex-row items-center justify-between gap-8">

            {/* Seçilmiş məhsullar */}
            <div className="flex items-center gap-4">
              <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-full">
                <svg
                  className="w-6 h-6 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8c-1.657 0-3 1.343-3 3v2h6v-2c0-1.657-1.343-3-3-3z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3c4.418 0 8 3.582 8 8v1H4v-1c0-4.418 3.582-8 8-8z"
                  />
                </svg>
              </div>

              <div>
                <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                  Seçilmiş məhsulların qiyməti
                </p>

                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {calculateTotalOrginalPrice} ₼
                </p>
              </div>
            </div>

            {/* Endirimli qiymət */}
            <div className="flex flex-col items-start gap-2">
              {calculateTotalOrginalPrice !==
              calculateTotalPrice ? (
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-green-100 dark:bg-green-900 rounded-full">
                    <svg
                      className="w-6 h-6 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8c-1.657 0-3 1.343-3 3v2h6v-2c0-1.657-1.343-3-3-3z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 3c4.418 0 8 3.582 8 8v1H4v-1c0-4.418 3.582-8 8-8z"
                      />
                    </svg>
                  </div>

                  <div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                      Endirimli qiymət (10%)
                    </p>

                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {calculateTotalPrice} ₼
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Bu məhsullarda hazırda endirim yoxdur.
                </p>
              )}
            </div>

            {/* Səbəti təsdiqlə */}
            <button
              type="button"
              onClick={handleConfirmCart}
              disabled={
                !isStockAvailable ||
                confirmingCart ||
                selectedItems.length === 0
              }
              className={`mt-4 md:mt-0 px-8 py-3 rounded-full text-lg font-semibold shadow-lg transition-all duration-300 ${
                !isStockAvailable ||
                confirmingCart ||
                selectedItems.length === 0
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              }`}
            >
              {confirmingCart ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Səbət təsdiqlənir...
                </span>
              ) : (
                "Səbəti təsdiqlə"
              )}
            </button>
          </div>

          {/* Stok xəbərdarlığı */}
          {selectedItems.length > 0 &&
            !isStockAvailable && (
              <div className="mt-5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">
                    ⚠️
                  </span>

                  <div>
                    <h3 className="font-bold text-yellow-800 dark:text-yellow-300 mb-1">
                      Səbəti təsdiqləmək mümkün deyil
                    </h3>

                    <p className="text-sm text-yellow-700 dark:text-yellow-200">
                      Seçdiyiniz məhsullardan ən azı biri
                      hazırda stokda yoxdur. Davam etmək üçün
                      stokda olmayan məhsulu seçimdən çıxarın.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default Basket;
